/**
 * Whether the Orders section is shown, which depends on the shop's hub.
 *
 * Hidden for the eBay hub only. Every new install is the eBay hub, so every
 * new install arrives without it — but a shop already running on `shopify` or
 * on `two_way` keeps the section exactly as it was, because those sellers have
 * been using it and nothing about their app is meant to change.
 *
 * STRICTLY 'ebay', AND THAT IS NOT PEDANTRY
 *
 * Dashboard already carries `ebayHub = hub !== 'shopify' && !!hub`, which is
 * TRUE for two_way as well — it exists to decide which way the catalogue
 * figures count, and for that two_way does behave like the eBay hub. Reusing
 * it here would have hidden Orders from every two-way seller. This asks the
 * narrower question, so the two must not be conflated.
 *
 * NOTHING IS SWITCHED OFF
 *
 * Order syncing is untouched under every hub: eBay orders are still pulled in,
 * the Shopify order is still created, failures are still recorded, and
 * pages/Orders.jsx, routes/orders.js and services/ebayFulfillment.js are all
 * as they were. This decides what the app PRESENTS, not what it does.
 *
 * WHAT READS THIS
 *
 *   components/Sidebar        the in-app nav, and the only nav in admin view
 *   components/ShopifyNavMenu the menu Shopify draws from App Bridge
 *   App.jsx                   the /orders route, via hooks/useOrdersHidden
 *   pages/Dashboard           revenue cards, the orders feed tab, the
 *                             "needs a SKU fix" strip and the attention card
 *
 * The route matters as much as the nav entries. Hiding a link leaves the path
 * typeable and bookmarked, and RouteGuard lets any path through on first
 * render while embedded, because Shopify owns the URL there. So the route
 * itself has to decide — see hooks/useOrdersHidden. RouteGuard's allowlist is
 * deliberately left alone: whether a path is routable is not a per-shop
 * question, and /orders is still a real route for the shops that have it.
 */

/** The one hub the section is hidden for. */
export const HUB_WITHOUT_ORDERS = 'ebay'

/**
 * Whether to hide Orders for this shop.
 *
 * An unknown hub — settings not loaded yet, or a value this build does not
 * recognise — answers false, so the section is shown. That is the same
 * direction the rest of the app errs in: "an entry that appears a moment late
 * is better than one that vanishes in front of whoever is looking at it."
 *
 * THE CUSTOM-APP SHOP KEEPS THE SECTION, WHATEVER ITS HUB
 *
 * `customApp` is migration 098's 'secondary': the custom app the first seller
 * is already on, and the only shop that predates the public app. This whole
 * change is about what a NEW install gets — that seller has been working with
 * Orders since before any of it, and their app is not meant to change.
 *
 * Their hub happens to be eBay, so without this they would have lost the
 * section, the Settings controls for it and its rows in the activity log on
 * the next deploy — a change nobody asked for, to the one shop that is
 * explicitly meant to be left alone. Keyed on the app identity rather than on
 * their domain, because which shop that is belongs in the database, not in a
 * deploy: `hide_pricing` in migration 067 says the same thing.
 */
export function ordersHiddenForShop(hub, customApp) {
  if (customApp) return false
  return hub === HUB_WITHOUT_ORDERS
}

/**
 * Kept as the hub-only question, for callers that have no app identity to
 * hand. Prefer ordersHiddenForShop: this one cannot protect the custom-app
 * shop, so anything deciding what a seller sees should pass both.
 */
export function ordersHiddenForHub(hub) {
  return hub === HUB_WITHOUT_ORDERS
}

/**
 * Drop order entries from a nav list, for this hub. Keeps the list intact.
 *
 * `pathOf` reads the path out of an entry, because the sidebar calls the field
 * `path` and the Shopify nav calls it `href`.
 */
export function withoutOrders(items, hub, customApp, pathOf = i => i.path || i.href) {
  return ordersHiddenForShop(hub, customApp)
    ? items.filter(i => pathOf(i) !== '/orders')
    : items
}
